import { cloneDeep } from 'lodash';
import { create } from 'zustand';
import { getState, useState } from '@/framework';
import { IExportState, ILayerProps, StateType } from '@/version-types';
import {
    defaultBaseEnhance,
    defaultColorEnhance,
    // defaultEnhanceParams,
    defaultHslEnhance,
    defaultLightEnhance,
    defaultParticularsEnhance,
    defaultPosterizeEnhance,
} from '../config';
import {
    IBaseEnhance,
    IColorEnhance,
    IHslEnhance,
    ILightEnhance,
    IParticularsEnhance,
    IPosterizeEnhance,
} from '../types';

/** 状态 */
export interface IState {
    originLayers: ILayerProps[];

    lastOriginLayers: ILayerProps[];
    /** 原始图层信息 */
    originLayer: ILayerProps | null;
    /** 基础调色 */
    baseEnhance: IBaseEnhance;
    /** 色彩 */
    colorEnhance: IColorEnhance;
    /** 光效 */
    lightEnhance: ILightEnhance;
    /** 细节 */
    particularsEnhance: IParticularsEnhance;
    /** 色调分离 */
    posterizeEnhance: IPosterizeEnhance;
    /** hsl */
    hslEnhance: IHslEnhance;
    /** 图片颜色提取，HSL 调色使用 */
    extractColors: string[];

    isBatchEditor: boolean;

    // /** 预设调色 */
    // enhanceInitData: {
    //     baseEnhance: IBaseEnhance;
    //     colorEnhance: IColorEnhance;
    //     hslEnhance: IHslEnhance;
    //     lightEnhance: ILightEnhance;
    //     particularsEnhance: IParticularsEnhance;
    //     posterizeEnhance: IPosterizeEnhance;
    // };
}

/** 动作 */
export interface IAction {
    setOriginLayers: (layers: ILayerProps[]) => void;
    setLastOriginLayers: (layers: ILayerProps[]) => void;
    setOriginLayer: (layer: ILayerProps | null) => void;
    setBaseEnhance: (baseEnhance: IBaseEnhance) => void;
    setColorEnhance: (colorEnhance: IColorEnhance) => void;
    setLightEnhance: (lightEnhance: ILightEnhance) => void;
    setParticularsEnhance: (particularsEnhance: IParticularsEnhance) => void;
    setPosterizeEnhance: (posterizeEnhance: IPosterizeEnhance) => void;
    setHslEnhance: (hslEnhance: IHslEnhance) => void;
    resetEnhance: () => void;
    setExtractColors: (colors: string[]) => void;
    setIsBatchEditor: (isBatchEditor: boolean) => void;
    destroy: () => void;
}

/** 存储类型 */
export interface IStore extends IState, IAction {}

/** 初始化状态 */
function getInitialState(): IState {
    return {
        originLayer: null,
        extractColors: [],
        baseEnhance: cloneDeep(defaultBaseEnhance),
        colorEnhance: cloneDeep(defaultColorEnhance),
        lightEnhance: cloneDeep(defaultLightEnhance),
        particularsEnhance: cloneDeep(defaultParticularsEnhance),
        posterizeEnhance: cloneDeep(defaultPosterizeEnhance),
        hslEnhance: cloneDeep(defaultHslEnhance),
        isBatchEditor: false,
        originLayers: [],
        lastOriginLayers: [],
        // enhanceInitData: cloneDeep(defaultEnhanceParams),
    };
}

/** 创建存储 */
const createState = (identity: string) => {
    const store = create<IStore>((set, get) => ({
        ...getInitialState(),
        setOriginLayer(layer) {
            set({ originLayer: layer });
        },
        setBaseEnhance(baseEnhance) {
            set({ baseEnhance: { ...baseEnhance } });
        },
        setColorEnhance(colorEnhance) {
            set({ colorEnhance: { ...colorEnhance } });
        },
        setLightEnhance(lightEnhance) {
            set({ lightEnhance: { ...lightEnhance } });
        },
        setParticularsEnhance(particularsEnhance) {
            set({ particularsEnhance: { ...particularsEnhance } });
        },
        setPosterizeEnhance(posterizeEnhance) {
            set({ posterizeEnhance: { ...posterizeEnhance } });
        },
        setHslEnhance(hslEnhance) {
            set({ hslEnhance: { ...hslEnhance } });
        },
        resetEnhance() {
            set({
                baseEnhance: cloneDeep(defaultBaseEnhance),
                colorEnhance: cloneDeep(defaultColorEnhance),
                lightEnhance: cloneDeep(defaultLightEnhance),
                particularsEnhance: cloneDeep(defaultParticularsEnhance),
                posterizeEnhance: cloneDeep(defaultPosterizeEnhance),
                hslEnhance: cloneDeep(defaultHslEnhance),
            });
        },
        setExtractColors(colors) {
            set({ extractColors: colors });
        },

        destroy() {
            set(getInitialState());
        },
        setIsBatchEditor(isBatchEditor) {
            set({ isBatchEditor });
        },
        setOriginLayers(layers) {
            set({ originLayers: layers });
        },
        setLastOriginLayers(layers) {
            set({ lastOriginLayers: layers });
        },
    }));

    return store;
};

/** 存储名称 */
const name = 'image-enhance-state';

/** 存储类型 */
type ModuleType = StateType<IStore, typeof name>;

/** hook 获取存储 */
export const useImageEnhanceState = () => {
    return useState<ModuleType>()[name];
};

/** 通过 id 获取存储 */
export const getImageEnhanceState = (identity: string) => {
    return getState<ModuleType>(identity)[name].getState();
};

export const watchImageEnhanceChange = (identity: string, callback: (state: IStore) => void) => {
    return getState<ModuleType>(identity)[name].subscribe((state, preState) => {
        if (JSON.stringify(preState) !== JSON.stringify(state)) {
            callback(state);
        }
    });
};

const exportState: IExportState = {
    name,
    createState,
};

export default exportState;
